<template>
  <div v-if="codes.length" class="mail-code-wrap">
    <button ref="trigger"
            :aria-expanded="open ? 'true' : 'false'"
            class="mail-code-btn"
            type="button"
            @click.stop="toggleOpen"
    >
      {{ tt("mail.add_variable", $t("mail.add_code")) }}
    </button>
    <div v-if="open"
         ref="panel"
         :style="panelStyle"
         class="mail-code-dropdown-panel"
         @mousedown.stop
         @mouseup.stop
         @click.stop
         @dragstart.prevent
    >
      <div class="mail-code-grid">
        <ul>
          <li v-for="code in codes"
              :key="code.code"
              class="mail-code-item"
          >
            <div class="mail-code-name">
              {{ variableLabel(code) }}
            </div>
            <div class="mail-code-value">
              <b>{{ code.code }}</b>
              <button type="button" @click.prevent.stop="copyCode(code.code)">
                <i class="uil-copy-alt"></i>
                {{
                  copiedCode === code.code
                      ? tt("general.copied", "Kopyalandı")
                      : $t("general.copy")
                }}
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
const PANEL_Z_INDEX = 1050;
const PANEL_WIDTH = 300;
const PANEL_MAX_HEIGHT = 320;
const PANEL_GAP = 8;
const VIEWPORT_PAD = 8;

export default {
  name: "MailCodeDropdown",
  props: {
    codes: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      open: false,
      copiedCode: null,
      copyTimer: null,
      panelStyle: {
        position: "fixed",
        top: "0px",
        left: "0px",
        zIndex: PANEL_Z_INDEX,
        visibility: "hidden",
        maxHeight: `${PANEL_MAX_HEIGHT}px`,
        overflowY: "auto",
      },
    };
  },
  methods: {
    tt(key, fallback) {
      if (this.$te && this.$te(key)) {
        const translated = this.$t(key);
        if (translated && translated !== key) return translated;
      }
      return fallback;
    },
    variableLabel(code) {
      const raw = String((code && code.code) || "")
          .replace(/[{}]/g, "")
          .toLowerCase();
      const compact = raw
          .replace(/ı/g, "i")
          .replace(/ş/g, "s")
          .replace(/ğ/g, "g")
          .replace(/ü/g, "u")
          .replace(/ö/g, "o")
          .replace(/ç/g, "c")
          .replace(/[._\-\s]/g, "");
      if (
          raw === "e.adi" ||
          raw === "e.adı" ||
          raw === "e_adi" ||
          compact === "eadi"
      ) {
        return this.tt("general.training_name", "Eğitim Adı");
      }
      if (code && code.key_name && this.$te && this.$te(code.key_name)) {
        return this.$t(code.key_name);
      }
      return (code && (code.name || code.code)) || "";
    },
    toggleOpen() {
      if (this.open) {
        this.close();
        return;
      }
      this.open = true;
      this.$nextTick(() => {
        this.ensurePortal();
        this.positionPanel();
        this.bindGlobal();
      });
    },
    close() {
      if (!this.open) return;
      this.open = false;
      this.unbindGlobal();
    },
    ensurePortal() {
      const panel = this.$refs.panel;
      if (panel && panel.parentNode !== document.body) {
        document.body.appendChild(panel);
      }
    },
    positionPanel() {
      const trigger = this.$refs.trigger;
      const panel = this.$refs.panel;
      if (!trigger || !panel) return;

      const rect = trigger.getBoundingClientRect();
      const panelWidth = Math.min(
          PANEL_WIDTH,
          window.innerWidth - VIEWPORT_PAD * 2
      );

      panel.style.width = `${panelWidth}px`;
      panel.style.maxHeight = `${PANEL_MAX_HEIGHT}px`;
      panel.style.overflowY = "auto";

      const panelHeight = Math.min(
          panel.offsetHeight || PANEL_MAX_HEIGHT,
          PANEL_MAX_HEIGHT
      );

      let left = rect.right - panelWidth;
      if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;
      if (left + panelWidth > window.innerWidth - VIEWPORT_PAD) {
        left = Math.max(
            VIEWPORT_PAD,
            window.innerWidth - panelWidth - VIEWPORT_PAD
        );
      }

      let top = rect.bottom + PANEL_GAP;
      if (top + panelHeight > window.innerHeight - VIEWPORT_PAD) {
        const above = rect.top - PANEL_GAP - panelHeight;
        if (above >= VIEWPORT_PAD) {
          top = above;
        } else {
          top = Math.max(
              VIEWPORT_PAD,
              window.innerHeight - panelHeight - VIEWPORT_PAD
          );
        }
      }

      this.panelStyle = {
        position: "fixed",
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        width: `${Math.round(panelWidth)}px`,
        maxHeight: `${PANEL_MAX_HEIGHT}px`,
        overflowY: "auto",
        zIndex: PANEL_Z_INDEX,
        visibility: "visible",
      };
    },
    bindGlobal() {
      this.unbindGlobal();
      this._onDocPointer = (event) => {
        const panel = this.$refs.panel;
        const trigger = this.$refs.trigger;
        if (panel && panel.contains(event.target)) return;
        if (trigger && trigger.contains(event.target)) return;
        this.close();
      };
      this._onKeydown = (event) => {
        if (event.key === "Escape" || event.keyCode === 27) {
          this.close();
        }
      };
      this._onReposition = () => {
        if (this.open) this.positionPanel();
      };
      document.addEventListener("mousedown", this._onDocPointer, true);
      document.addEventListener("keydown", this._onKeydown, true);
      window.addEventListener("resize", this._onReposition);
      window.addEventListener("scroll", this._onReposition, true);
    },
    unbindGlobal() {
      if (this._onDocPointer) {
        document.removeEventListener("mousedown", this._onDocPointer, true);
        this._onDocPointer = null;
      }
      if (this._onKeydown) {
        document.removeEventListener("keydown", this._onKeydown, true);
        this._onKeydown = null;
      }
      if (this._onReposition) {
        window.removeEventListener("resize", this._onReposition);
        window.removeEventListener("scroll", this._onReposition, true);
        this._onReposition = null;
      }
    },
    ensureOpenPortal() {
      if (this.open) this.ensurePortal();
    },
    copyCode(code) {
      const value = String(code || "");
      if (!value) return;

      const el = document.createElement("textarea");
      el.value = value;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);

      const selection = document.getSelection();
      const selected =
          selection && selection.rangeCount > 0
              ? selection.getRangeAt(0)
              : false;

      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);

      if (selected && selection) {
        selection.removeAllRanges();
        selection.addRange(selected);
      }

      this.copiedCode = value;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        this.copiedCode = null;
      }, 1200);
    },
  },
  updated() {
    this.ensureOpenPortal();
  },
  beforeDestroy() {
    this.unbindGlobal();
    clearTimeout(this.copyTimer);
    const panel = this.$refs.panel;
    if (panel && panel.parentNode) {
      panel.parentNode.removeChild(panel);
    }
  },
};
</script>

<style scoped>
.mail-code-wrap {
  position: relative;
  z-index: 10;
  overflow: visible;
  flex-shrink: 0;
}

.mail-code-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  border: 1px solid rgba(91, 79, 255, 0.18);
  border-radius: 8px;
  background: linear-gradient(135deg, #f8f6ff 0%, #fff 100%);
  color: var(--mail-primary, #5b4fff);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 1px 3px rgba(91, 79, 255, 0.06);
}

.mail-code-btn:hover {
  background: linear-gradient(135deg, #f0edff 0%, #f8f6ff 100%);
  border-color: rgba(91, 79, 255, 0.3);
  box-shadow: 0 3px 10px rgba(91, 79, 255, 0.12);
}

.mail-code-btn:active {
  box-shadow: 0 1px 2px rgba(91, 79, 255, 0.08);
}

@media (max-width: 767px) {
  .mail-code-wrap {
    display: flex;
    justify-content: flex-end;
  }
}
</style>

<style>
/* Unscoped: panel is appended to document.body. */
.mail-code-dropdown-panel {
  --mail-primary: #5b4fff;
  box-sizing: border-box;
  width: 300px;
  max-width: calc(100vw - 16px);
  max-height: 320px;
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 1050;
  padding: 6px;
  border: 1px solid #e8eaf3;
  border-radius: 12px;
  background: #fff;
  color: #666;
  box-shadow: 0 12px 32px rgba(23, 26, 47, 0.12),
              0 2px 6px rgba(23, 26, 47, 0.04);
}

.mail-code-dropdown-panel::-webkit-scrollbar {
  width: 5px;
}

.mail-code-dropdown-panel::-webkit-scrollbar-track {
  background: transparent;
}

.mail-code-dropdown-panel::-webkit-scrollbar-thumb {
  background: #d4d7e3;
  border-radius: 3px;
}

.mail-code-dropdown-panel .mail-code-grid {
  display: grid;
  grid-template-columns: 1fr;
}

.mail-code-dropdown-panel .mail-code-grid ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mail-code-dropdown-panel .mail-code-item {
  margin: 0;
  padding: 10px 12px;
  list-style: none;
  border-bottom: 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.mail-code-dropdown-panel .mail-code-item:hover {
  background: #f4f2ff;
}

.mail-code-dropdown-panel .mail-code-name {
  margin-bottom: 5px;
  color: #2a2f4a;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}

.mail-code-dropdown-panel .mail-code-value {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  min-width: 0;
  margin: 0;
}

.mail-code-dropdown-panel .mail-code-value b {
  min-width: 0;
  overflow: hidden;
  padding: 3px 8px;
  background: #f4f5fa;
  border-radius: 5px;
  color: #3a3f5c;
  font-size: 12px;
  font-weight: 600;
  font-family: "SF Mono", "Fira Code", "Consolas", monospace;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: all;
}

.mail-code-dropdown-panel .mail-code-value button {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border: 1px solid rgba(91, 79, 255, 0.15);
  border-radius: 6px;
  background: linear-gradient(135deg, #f8f6ff 0%, #fff 100%);
  color: var(--mail-primary, #5b4fff);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.18s ease;
}

.mail-code-dropdown-panel .mail-code-value button:hover {
  border-color: rgba(91, 79, 255, 0.3);
  background: linear-gradient(135deg, #f0edff 0%, #f8f6ff 100%);
  color: #5b4fff;
  box-shadow: 0 2px 6px rgba(91, 79, 255, 0.12);
}

.mail-code-dropdown-panel .mail-code-value button:active {
  box-shadow: none;
}

.mail-code-dropdown-panel .mail-code-value button i {
  font-size: 13px;
  line-height: 1;
}
</style>
